import { getBackendUrl } from "@Utils";
import { t } from "@i18n";
import axiosWrapper from "../../../../services/axiosWrapper";
export { setReviewFlowResourceType } from "../../../../reducers/ReviewFlows";

// Import shared types from server package - SINGLE SOURCE OF TRUTH
// These types are inferred from Zod schemas in the server reviewFlows schema
import type {
  SaveTransitionRequest,
  SaveTransitionResponse,
  GetTransitionRequest,
  GetTransitionResponse,
  GetStatusesInTransitionsRequest,
  GetStatusesInTransitionsResponse,
  ReviewLevelUIState,
  TransitionSelectionState,
  TransitionResult,
  // Save ReviewFlow types - discriminated union for strict typing
  CreateReviewFlowRequest,
  UpdateReviewFlowRequest,
  SaveReviewFlowRequest,
  SaveReviewFlowResponse,
  ReviewFlowResourceType,
  // Add Reviewer to Level types - strict typing for reviewer management
  AddReviewerToLevelRequest,
  AddReviewerToLevelResponse,
  // Remove Reviewer from Level types
  RemoveReviewerFromLevelRequest,
  RemoveReviewerFromLevelResponse,
  // Duplicate ReviewFlow types
  DuplicateReviewFlowRequest,
  DuplicateReviewFlowResponse,
  // Delete ReviewFlows types
  DeleteReviewFlowsRequest,
  DeleteReviewFlowsResponse,
  // Additional response types
  SaveReviewLevelRequest,
  SaveReviewLevelResponse,
  GetReviewFlowReviewLevelDataRequest,
  AddInitiatorToReviewFlowRequest,
  RemoveInitiatorFromReviewFlowRequest,
  ChangeArchivedReviewFlowStatusRequest,
  AddReviewFlowsToLibraryRequest,
  GetStatusListInReviewFlowRequest,
  SaveReviewFlowStatusListRequest,
  ReviewFlowReviewLevelsServiceResponse,
  GetReviewFlowReviewLevelDataResponse,
  AddInitiatorToReviewFlowResponse,
  RemoveInitiatorFromReviewFlowResponse,
  ChangeArchivedReviewFlowStatusResponse,
  AddReviewFlowsToLibraryResponse,
  TransitionsServiceResponse,
  GetStatusListInReviewFlowResponse,
  SaveReviewFlowStatusListResponse,
} from "@ServerTypes/reviewflows/reviewflows.schema.js";
import type {
  ReviewsServiceRequest,
  ReviewsServiceResponse,
  GetChecklistsResponse,
} from "@ServerTypes/reviews/reviews.schema.js";
import type { GetPortalLevelStatusListResponse } from "@ServerTypes/portal/portal.schema.js";

// Re-export types for use in components
export type {
  SaveTransitionRequest,
  SaveTransitionResponse,
  GetTransitionRequest,
  GetTransitionResponse,
  GetStatusesInTransitionsRequest,
  GetStatusesInTransitionsResponse,
  ReviewLevelUIState,
  TransitionSelectionState,
  TransitionResult,
  // Save ReviewFlow types
  CreateReviewFlowRequest,
  UpdateReviewFlowRequest,
  SaveReviewFlowRequest,
  SaveReviewFlowResponse,
  ReviewFlowResourceType,
  // Add Reviewer to Level types
  AddReviewerToLevelRequest,
  AddReviewerToLevelResponse,
  // Remove Reviewer from Level types
  RemoveReviewerFromLevelRequest,
  RemoveReviewerFromLevelResponse,
  // Duplicate ReviewFlow types
  DuplicateReviewFlowRequest,
  DuplicateReviewFlowResponse,
  // Delete ReviewFlows types
  DeleteReviewFlowsRequest,
  DeleteReviewFlowsResponse,
};

export interface ApiResponse<T = any> {
  data: T;
  status: number;
}

/**
 * Accepts any cell/toolbar template function. The Syncfusion-era column and toolbar builders below
 * are handed templates with unrelated argument shapes, so the parameter list is left open.
 */
type AnyGridTemplate = (...args: never[]) => unknown;

export interface TemplateArgs {
  createdByTemplate?: AnyGridTemplate;
  createdDateTemplate?: AnyGridTemplate;
  statusFromTemplate?: AnyGridTemplate;
  statusToTemplate?: AnyGridTemplate;
  reviewLevelsTemplate?: AnyGridTemplate;
  reviewLevelTemplate?: AnyGridTemplate;
  reviewersTemplate?: AnyGridTemplate;
  reviewLevelFilterTemplate?: AnyGridTemplate;
}

export interface ToolbarTemplateArgs {
  [key: string]: AnyGridTemplate | undefined;
}

/**
 * Entry of a Syncfusion-era toolbar array: either a configured button or a built-in command name
 * such as `"ColumnChooser"`.
 */
type LegacyToolbarEntry = { id: string; align: "Left" | "Right"; template?: AnyGridTemplate } | string;

/**
 * Save (create or update) a reviewFlow.
 * Uses discriminated union type for strict compile-time validation.
 *
 * @param data - CreateReviewFlowRequest (without _id) or UpdateReviewFlowRequest (with _id)
 * @returns Typed response with status and optional error message
 *
 * @example
 * // Create new reviewFlow
 * await saveReviewFlow({ name: "My ReviewFlow", spaceId: "...", description: "..." });
 *
 * @example
 * // Update existing reviewFlow
 * await saveReviewFlow({ _id: "...", name: "Updated Name", checklistIds: ["..."] });
 */
export const saveReviewFlow = async (data: SaveReviewFlowRequest): Promise<ApiResponse<SaveReviewFlowResponse>> =>
  await axiosWrapper.post<SaveReviewFlowResponse>(`${getBackendUrl()}/reviewflows/saveReviewFlow`, data);

/**
 * Delete one or more reviewFlows by their IDs.
 * @param data - Typed request with ids array and optional projectId
 * @returns Typed response with status
 */
export const deleteReviewFlows = async (
  data: DeleteReviewFlowsRequest,
): Promise<ApiResponse<DeleteReviewFlowsResponse>> =>
  await axiosWrapper.post<DeleteReviewFlowsResponse>(`${getBackendUrl()}/reviewflows/deleteReviewFlows`, data);

export const saveReviewLevel = async (data: SaveReviewLevelRequest): Promise<ApiResponse> =>
  await axiosWrapper.post<SaveReviewLevelResponse>(`${getBackendUrl()}/reviewflows/saveReviewLevel`, data);

/**
 * `unknown` request body, deliberately: `reviewFlowReviewLevelsDefaultSchema` declares `key`,
 * `params` AND `value` as required (for shape parity with the /removeReviewLevel path) while the
 * LIST handler reads only `value`, which is all any caller sends. Typing the parameter to the schema
 * would force callers — including one outside this module — to send fields the server ignores. The
 * body is forwarded verbatim to axios, so `unknown` is sufficient here.
 */
export const fetchReviewFlowLevels = async (data: unknown): Promise<ApiResponse> =>
  await axiosWrapper.post<ReviewFlowReviewLevelsServiceResponse>(
    `${getBackendUrl()}/reviewflows/reviewFlowReviewLevelsService`,
    data,
  );

export const fetchreviewsUrl = async (data: ReviewsServiceRequest): Promise<ApiResponse> =>
  await axiosWrapper.post<ReviewsServiceResponse>(`${getBackendUrl()}/reviews/reviewsService`, data);

/**
 * `unknown` request body: the only caller lives outside this module and passes a plain-`string` id,
 * whereas `getChecklistsRequest._id` is a branded `objectIdString`. Tightening here would push a type
 * error into a file this change is not allowed to touch.
 */
export const fetchchecklistData = async (data: unknown): Promise<ApiResponse> =>
  await axiosWrapper.post<GetChecklistsResponse>(`${getBackendUrl()}/reviews/getChecklists`, data);

export const getReviewLevelData = async (data: GetReviewFlowReviewLevelDataRequest): Promise<ApiResponse> =>
  await axiosWrapper.post<GetReviewFlowReviewLevelDataResponse>(
    `${getBackendUrl()}/reviewflows/getReviewFlowReviewLevelData`,
    data,
  );

/**
 * Add reviewer(s) to a reviewFlow review level.
 * @param data - Typed request with levelId, reviewFlowId, reviewerIds, and optional transitionUsers
 * @returns Typed response with status and optional error message
 */
export const addReviewerToLevel = async (
  data: AddReviewerToLevelRequest,
): Promise<ApiResponse<AddReviewerToLevelResponse>> =>
  await axiosWrapper.post<AddReviewerToLevelResponse>(`${getBackendUrl()}/reviewflows/addReviewerToLevel`, data);

export const addInitiatorToReviewFlow = async (data: AddInitiatorToReviewFlowRequest): Promise<ApiResponse> =>
  await axiosWrapper.post<AddInitiatorToReviewFlowResponse>(
    `${getBackendUrl()}/reviewflows/addInitiatorToReviewFlow`,
    data,
  );

/**
 * Remove a reviewer from a reviewFlow review level.
 * @param data - Typed request with levelId, reviewFlowId, and reviewerId
 * @returns Typed response with status and optional error message
 */
export const removeReviewerFromLevel = async (
  data: RemoveReviewerFromLevelRequest,
): Promise<ApiResponse<RemoveReviewerFromLevelResponse>> =>
  await axiosWrapper.post<RemoveReviewerFromLevelResponse>(
    `${getBackendUrl()}/reviewflows/removeReviewerFromLevel`,
    data,
  );

export const removeInitiatorFromReviewFlow = async (data: RemoveInitiatorFromReviewFlowRequest): Promise<ApiResponse> =>
  await axiosWrapper.post<RemoveInitiatorFromReviewFlowResponse>(
    `${getBackendUrl()}/reviewflows/removeInitiatorFromReviewFlow`,
    data,
  );

/**
 * Duplicate one or more reviewFlows to a space.
 * @param data - Typed request with reviewFlowIds, spaceId, and optional flags
 * @returns Typed response with status
 */
export const duplicateReviewFlow = async (
  data: DuplicateReviewFlowRequest,
): Promise<ApiResponse<DuplicateReviewFlowResponse>> =>
  await axiosWrapper.post<DuplicateReviewFlowResponse>(`${getBackendUrl()}/reviewflows/duplicateReviewFlow`, data);

export const changeArchivedReviewFlowStatus = async (
  data: ChangeArchivedReviewFlowStatusRequest,
): Promise<ApiResponse> =>
  await axiosWrapper.post<ChangeArchivedReviewFlowStatusResponse>(
    `${getBackendUrl()}/reviewflows/changeArchivedReviewFlowStatus`,
    data,
  );

export const addReviewFlowsToLibrary = async (data: AddReviewFlowsToLibraryRequest): Promise<ApiResponse> =>
  await axiosWrapper.post<AddReviewFlowsToLibraryResponse>(
    `${getBackendUrl()}/reviewflows/addReviewFlowsToLibrary`,
    data,
  );

/**
 * Save (create or update) a reviewFlow transition.
 * @param data - Typed request payload validated against shared Zod schema
 * @returns Typed response with status and optional error message
 */
export const saveTransition = async (data: SaveTransitionRequest): Promise<ApiResponse<SaveTransitionResponse>> =>
  await axiosWrapper.post<SaveTransitionResponse>(`${getBackendUrl()}/reviewflows/saveTransition`, data);

/**
 * `unknown` request body: `getPortalLevelStatusListRequest` makes `value.where` required, but the
 * caller sends only `value.skip`/`value.take`. The body is forwarded verbatim to axios.
 */
export const getPortalLevelStatusList = async (data: unknown): Promise<ApiResponse> =>
  await axiosWrapper.post<GetPortalLevelStatusListResponse>(`${getBackendUrl()}/portal/getPortalLevelStatusList`, data);

/**
 * Get a specific transition by ID.
 * @param data - Typed request with transitionId and reviewFlowId
 * @returns Typed response with transition result
 */
export const getTransition = async (data: GetTransitionRequest): Promise<ApiResponse<GetTransitionResponse>> =>
  await axiosWrapper.post<GetTransitionResponse>(`${getBackendUrl()}/reviewflows/getTransition`, data);

/**
 * `unknown` request body: like the review-levels list, `transitionsServiceRequest` declares
 * `key`/`params`/`value` all required for parity with /removeTransition while the LIST handler reads
 * only `value`. The body is forwarded verbatim to axios.
 */
export const getTransitionsList = async (data: unknown): Promise<ApiResponse> =>
  await axiosWrapper.post<TransitionsServiceResponse>(`${getBackendUrl()}/reviewflows/transitionsService`, data);

export const getStatusListInReviewFlow = async (data: GetStatusListInReviewFlowRequest): Promise<ApiResponse> =>
  await axiosWrapper.post<GetStatusListInReviewFlowResponse>(
    `${getBackendUrl()}/reviewflows/getStatusListInReviewFlow`,
    data,
  );

/**
 * Get statuses used in transitions for a reviewFlow.
 * @param data - Typed request with reviewFlowId and resourceType
 * @returns Typed response with status list
 */
export const getStatusListInTransitions = async (
  data: GetStatusesInTransitionsRequest,
): Promise<ApiResponse<GetStatusesInTransitionsResponse>> =>
  await axiosWrapper.post<GetStatusesInTransitionsResponse>(
    `${getBackendUrl()}/reviewflows/getStatusesInTransitions`,
    data,
  );

export const saveReviewFlowStatusList = async (data: SaveReviewFlowStatusListRequest): Promise<ApiResponse> =>
  await axiosWrapper.post<SaveReviewFlowStatusListResponse>(
    `${getBackendUrl()}/reviewflows/saveReviewFlowStatusList`,
    data,
  );

// ---------- GRID COLUMNS ----------

export const reviewFlowGridColumns = ({ createdByTemplate, createdDateTemplate }: TemplateArgs) => [
  {
    type: "checkbox",
    width: "30",
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "_id",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    isPrimaryKey: true,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "name",
    headerText: t("Name"),
    allowFiltering: true,
    allowEditing: false,
    allowSorting: true,
  },
  {
    field: "description",
    headerText: t("Description"),
    allowFiltering: true,
    allowEditing: false,
    allowSorting: true,
  },
  {
    field: "createdBy",
    headerText: t("Created by"),
    type: "string",
    template: createdByTemplate,
    editTemplate: createdByTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: true,
  },
  {
    field: "createdDate",
    headerText: t("Created On"),
    template: createdDateTemplate,
    editTemplate: createdDateTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: true,
  },
];

// ---------- TOOLBARS ----------

export const getReviewFlowToolbar = ({
  editBtnTemplate,
  deleteBtnTemplate,
  duplicateBtnTemplate,
  addToLibraryBtnTemplate,
  craeteReviewFlowCopyBtnTemplate,
  archiveBtnTemplate,
  viewArchiveAndUnarchiveReviewFlowTemplate,
  isArchived,
}: ToolbarTemplateArgs & { isArchived: boolean }) => {
  const toolbar: LegacyToolbarEntry[] = [];

  if (!isArchived) {
    toolbar.push(
      { id: "create_review_flow_copy_dropdown", align: "Left" as const, template: craeteReviewFlowCopyBtnTemplate },
      { id: "edit_reviewFlow", align: "Left" as const, template: editBtnTemplate },
      { id: "delete_reviewFlow", align: "Left" as const, template: deleteBtnTemplate },
      { id: "duplicate_reviewFlow", align: "Left" as const, template: duplicateBtnTemplate },
      { id: "archive_reviewFlow", align: "Left" as const, template: archiveBtnTemplate },
      { id: "add_to_library", align: "Left" as const, template: addToLibraryBtnTemplate },
    );
  } else {
    toolbar.push(
      { id: "delete_reviewFlow", align: "Left" as const, template: deleteBtnTemplate },
      { id: "archive_reviewFlow", align: "Left" as const, template: archiveBtnTemplate },
    );
  }

  toolbar.push(
    {
      id: "view_Archive_And_Unarchive_ReviewFlow",
      align: "Right" as const,
      template: viewArchiveAndUnarchiveReviewFlowTemplate,
    },
    "ColumnChooser",
  );

  return toolbar;
};

export const getTransitionsToolbar = ({
  createBtnTemplate,
  editBtnTemplate,
  deleteBtnTemplate,
}: ToolbarTemplateArgs) => [
  { id: "create_transition", align: "Left" as const, template: createBtnTemplate },
  { id: "edit_transition", align: "Left" as const, template: editBtnTemplate },
  { id: "delete_transition", align: "Left" as const, template: deleteBtnTemplate },
  "ColumnChooser",
];

export const transitionsGridColumns = ({
  statusFromTemplate,
  statusToTemplate,
  reviewLevelsTemplate,
}: TemplateArgs) => [
  {
    type: "checkbox",
    width: "40",
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    width: 0,
    field: "_id",
    headerText: "",
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
    showInColumnChooser: false,
    isPrimaryKey: true,
    visible: false,
  },
  {
    field: "name",
    headerText: t("Transition Name"),
    allowFiltering: true,
    allowEditing: false,
    allowSorting: true,
  },
  {
    field: "description",
    headerText: t("Description"),
    allowFiltering: true,
    allowEditing: false,
    allowSorting: true,
  },
  {
    field: "statusFrom",
    headerText: t("From Status"),
    template: statusFromTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "statusTo",
    headerText: t("To Status"),
    template: statusToTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "levels",
    headerText: t("Levels"),
    width: "350",
    template: reviewLevelsTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
];

export const getReviewLevelsGridColumns = ({
  reviewLevelTemplate,
  reviewersTemplate,
  reviewLevelFilterTemplate,
}: TemplateArgs) => [
  {
    type: "checkbox",
    width: "25",
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    width: 0,
    field: "_id",
    headerText: "",
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
    showInColumnChooser: false,
    isPrimaryKey: true,
    visible: false,
  },
  {
    field: "serial",
    headerText: t("Review Levels"),
    filter: { type: "Menu" },
    width: "50",
    type: "number",
    template: reviewLevelTemplate,
    filterTemplate: reviewLevelFilterTemplate,
    allowFiltering: true,
    allowEditing: false,
    allowSorting: true,
  },
  {
    field: "title",
    width: "80",
    headerText: t("Level Title"),
    allowFiltering: true,
    allowEditing: false,
    allowSorting: true,
  },
  {
    field: "reviewers",
    headerText: t("Reviewers"),
    template: reviewersTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
];

export const getReviewFlowReviewLevelsToolbar = ({
  createBtnTemplate,
  editBtnTemplate,
  deleteBtnTemplate,
}: ToolbarTemplateArgs) => [
  { id: "create_review_level", template: createBtnTemplate, align: "Left" as const },
  { id: "edit_review_level", template: editBtnTemplate, align: "Left" as const },
  { id: "delete_review_level", template: deleteBtnTemplate, align: "Left" as const },
  "ColumnChooser",
];

// ---------- LEVEL STATE HELPERS ----------
// Using shared types from helpers package for consistency

// Local type aliases for backward compatibility and convenience
type ReviewLevel = ReviewLevelUIState;

/**
 * Handle checkbox change for an entire review level.
 * Toggles all reviewers in the level based on current state.
 */
export const levelCheckboxChange = (
  reviewLevels: ReviewLevel[],
  targetLevelId: string,
): { levelsUpdated: ReviewLevel[]; areReviewLevelsValid: boolean } => {
  let areReviewLevelsValid = false;

  const levelsUpdated = reviewLevels.map((level) => {
    const { _id, levelSelectionState, reviewers } = level;

    if (_id !== targetLevelId) {
      if (levelSelectionState === "partial" || levelSelectionState === "complete") {
        areReviewLevelsValid = true;
      }
      return level;
    }

    if (levelSelectionState === "empty" || levelSelectionState === "partial") {
      const updatedReviewers = reviewers.map((r) => ({ ...r, isReviewerSelected: true }));
      areReviewLevelsValid = true;
      return { ...level, levelSelectionState: "complete" as TransitionSelectionState, reviewers: updatedReviewers };
    }

    if (levelSelectionState === "complete") {
      const updatedReviewers = reviewers.map((r) => ({ ...r, isReviewerSelected: false }));
      return { ...level, levelSelectionState: "empty" as TransitionSelectionState, reviewers: updatedReviewers };
    }

    return level;
  });

  return { levelsUpdated, areReviewLevelsValid };
};

/**
 * Calculate the selection state based on how many reviewers are selected.
 */
const calculateLevelSelectionState = (selectedCount: number, totalCount: number): TransitionSelectionState => {
  if (selectedCount === 0) return "empty";
  if (selectedCount < totalCount) return "partial";
  return "complete";
};

/**
 * Handle checkbox change for an individual reviewer.
 * Updates the level's selection state based on the new reviewer selections.
 */
export const reviewerCheckboxChange = (
  reviewLevels: ReviewLevel[],
  targetLevelId: string,
  userId: string,
): { levelsUpdated: ReviewLevel[]; areReviewLevelsValid: boolean } => {
  let areReviewLevelsValid = false;

  const levelsUpdated = reviewLevels.map((level) => {
    const { _id, reviewers, levelSelectionState } = level;

    if (_id !== targetLevelId) {
      if (levelSelectionState === "partial" || levelSelectionState === "complete") {
        areReviewLevelsValid = true;
      }
      return level;
    }

    const updatedReviewers = reviewers.map((r) =>
      r.userId === userId ? { ...r, isReviewerSelected: !r.isReviewerSelected } : r,
    );

    const selectedCount = updatedReviewers.filter((r) => r.isReviewerSelected).length;
    const updatedState = calculateLevelSelectionState(selectedCount, updatedReviewers.length);

    if (updatedState === "partial" || updatedState === "complete") {
      areReviewLevelsValid = true;
    }

    return { ...level, levelSelectionState: updatedState, reviewers: updatedReviewers };
  });

  return { levelsUpdated, areReviewLevelsValid };
};
